import { Button, Input } from '@/src/presentation/components';
import { useTranslation } from '@/src/i18n';
import { ActivityIndicator, Image, KeyboardAvoidingView, Platform, Text, TouchableOpacity, View } from 'react-native';
import { createStyles } from './ResetPasswordScreen.styles';

export interface ResetPasswordScreenProps {
  newPassword: string;
  setNewPassword: (value: string) => void;
  confirmPassword: string;
  setConfirmPassword: (value: string) => void;
  loading: boolean;
  verifying: boolean;
  verifyError: string | null;
  handleUpdatePassword: () => void;
  navigateToLogin: () => void;
  colors: {
    background: string;
    text: string;
    textSecondary: string;
    textTertiary: string;
    card: string;
    border: string;
    primary: string;
    primaryText: string;
    error: string;
  };
}

export const ResetPasswordScreen = ({
  newPassword,
  setNewPassword,
  confirmPassword,
  setConfirmPassword,
  loading,
  verifying,
  verifyError,
  handleUpdatePassword,
  navigateToLogin,
  colors,
}: ResetPasswordScreenProps) => {
  const styles = createStyles();
  const { t } = useTranslation();

  if (verifying) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background, justifyContent: 'center', alignItems: 'center' }]}>
        <ActivityIndicator size="large" color={colors.primary} />
      </View>
    );
  }

  if (verifyError) {
    return (
      <View style={[styles.container, { backgroundColor: colors.background, justifyContent: 'center', alignItems: 'center', padding: 20 }]}>
        <View style={styles.errorContainer}>
          <Text style={[styles.errorText, { color: colors.error }]}>{verifyError}</Text>
          <TouchableOpacity onPress={navigateToLogin}>
            <Text style={[styles.backButtonText, { color: colors.primary }]}>{t('auth.forgotPassword')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      style={[styles.container, { backgroundColor: colors.background }]}
    >
      <View style={styles.content}>
        <View style={styles.logoContainer}>
          <Image source={require('@/assets/images/android-icon-foreground.png')} style={styles.logoImage} />
        </View>
        <Text style={[styles.title, { color: colors.text }]}>{t('auth.setNewPassword')}</Text>
        <Text style={[styles.subtitle, { color: colors.textSecondary }]}>{t('auth.enterNewPassword')}</Text>

        <View style={styles.form}>
          <Input
            placeholder={t('auth.newPassword')}
            value={newPassword}
            onChangeText={setNewPassword}
            secureTextEntry
            editable={!loading}
            colors={colors}
          />
          <Input
            placeholder={t('auth.confirmNewPassword')}
            value={confirmPassword}
            onChangeText={setConfirmPassword}
            secureTextEntry
            editable={!loading}
            colors={colors}
          />

          <Button
            title={t('auth.updatePassword')}
            onPress={handleUpdatePassword}
            disabled={loading}
            loading={loading}
            colors={colors}
          />

          <TouchableOpacity style={styles.backButton} onPress={navigateToLogin}>
            <Text style={[styles.backButtonText, { color: colors.primary }]}>{t('auth.alreadyHaveAccount')} {t('auth.signIn')}</Text>
          </TouchableOpacity>
        </View>
      </View>
    </KeyboardAvoidingView>
  );
};
